import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CircleParking, LayoutDashboard, MapPin, IndianRupee, Users, Building2, Landmark, FileBarChart, Settings as SettingsIcon } from "lucide-react";
import { PageHeader, EmptyState } from "../../../components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { useRoleModules } from "../../../lib/roleModules";
import { useLive } from "../../../lib/useLive";
import { ParkingLiveBadge } from "../../../components/parking/common";
import { OverviewTab } from "./OverviewTab";
import { LocationsTab } from "./LocationsTab";
import { TariffsTab } from "./TariffsTab";
import { MarshalsTab } from "./MarshalsTab";
import { CorporateTab } from "./CorporateTab";
import { SettlementsTab } from "./SettlementsTab";
import { ReportsTab } from "./ReportsTab";
import { SettingsTab } from "./SettingsTab";

/* Phase 200 — Municipal Admin → Smart Parking. Live overview + full dynamic configuration (locations, tariffs,
   marshals & shifts, corporate passes, settlements, reports, settings). Visible only when the Super Admin has assigned
   the Smart Parking Management service to this municipality (tenant module `smart_parking`). */

const TABS = [
  { value: "overview", label: "Overview", icon: LayoutDashboard },
  { value: "locations", label: "Locations", icon: MapPin },
  { value: "tariffs", label: "Tariffs", icon: IndianRupee },
  { value: "marshals", label: "Marshals & Shifts", icon: Users },
  { value: "corporate", label: "Corporate Passes", icon: Building2 },
  { value: "settlements", label: "Settlements", icon: Landmark },
  { value: "reports", label: "Reports", icon: FileBarChart },
  { value: "settings", label: "Settings", icon: SettingsIcon },
];

export default function SmartParking() {
  const { code } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.value === params.get("tab")) ? params.get("tab") : "overview";
  const setTab = useCallback((v) => { const p = new URLSearchParams(params); p.set("tab", v); setParams(p, { replace: true }); }, [params, setParams]);
  const access = useRoleModules(code);
  const canWrite = access.canWrite("smart_parking");
  const live = useLive(code, "admin");
  const [locations, setLocations] = useState(null);
  // locations are shared by several tabs (selects, map); refreshed whenever the live counter moves
  const reloadLocations = useCallback(() => import("../../../lib/api").then(({ api }) => api.get(`/t/${code}/parking/admin/locations`).then((r) => setLocations(r.data)).catch(() => {})), [code]);
  useEffect(() => { reloadLocations(); }, [reloadLocations, live]);

  if (access.tenantModulesLoaded && !access.tenantModule("smart_parking")) {
    return (
      <div data-testid="smart-parking-inactive">
        <PageHeader title="Smart Parking" subtitle="App-based parking management" />
        <EmptyState title="Smart Parking is not activated for this municipality"
          subtitle="Ask the Platform Super Admin to assign the 'Smart Parking Management' service from Master Services. Locations, tariffs and marshals can be configured here once it is active." />
      </div>
    );
  }

  return (
    <div data-testid="smart-parking-page">
      <PageHeader title={<span className="flex items-center gap-2"><CircleParking className="h-6 w-6 text-primary" /> Smart Parking</span>}
        subtitle="Live occupancy, bookings, marshals, passes and revenue — every rate and location is editable here">
        <ParkingLiveBadge />
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto" data-testid="parking-tabs">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`parking-tab-${t.value}`}><t.icon className="h-4 w-4 mr-1.5" /> {t.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview"><OverviewTab code={code} live={live} locations={locations} canWrite={canWrite} onGoTo={setTab} /></TabsContent>
        <TabsContent value="locations"><LocationsTab code={code} live={live} canWrite={canWrite} onChanged={reloadLocations} /></TabsContent>
        <TabsContent value="tariffs"><TariffsTab code={code} canWrite={canWrite} /></TabsContent>
        <TabsContent value="marshals"><MarshalsTab code={code} live={live} canWrite={canWrite} /></TabsContent>
        <TabsContent value="corporate"><CorporateTab code={code} live={live} locations={locations} canWrite={canWrite} /></TabsContent>
        <TabsContent value="settlements"><SettlementsTab code={code} live={live} canWrite={canWrite} /></TabsContent>
        <TabsContent value="reports"><ReportsTab code={code} live={live} /></TabsContent>
        <TabsContent value="settings"><SettingsTab code={code} canWrite={canWrite} /></TabsContent>
      </Tabs>
    </div>
  );
}
